import React, { useContext, useEffect, useState } from 'react';
import { PurchaseOrderDocument } from '../graphql/entities';
import DocumentModal2 from '../components/modals/DocumentModal2';
import Fieldset from '../components/connectedForm/Fieldset';
import ToggleInput from '../components/connectedForm/ToggleInput/ToggleInput';
import { useDocumentConfig } from './FetchConfig';
import { useBackgroundMutation } from './BackgroundJobs';
import {
  IPurchaseOrderInput,
  IPurchaseOrderLineInput,
  ISendEdiPurchaseOrderInput,
  ISendEdiPurchaseOrderMutationVariables,
} from '../graphql/schema/p4types';
import SendEdiPurchaseOrderMutation from '../graphql/mutations/purchases/SendEdiPurchaseOrderMutation.graphql';
import normalizeVariables from '../graphql/schema/normalizeVariables';
import generateClientMutationId from '../graphql/schema/generateClientMutationId';
import WorkspaceContext from '../components/Context/WorkspaceContext';

export const submitValues = ({ values }: { values: IPurchaseOrderInput }, temporary) => {
  const purchaseOrder = Object.assign({}, values);

  if (!temporary) {
    purchaseOrder.orderLines =
      purchaseOrder.orderLines?.filter(
        (line: IPurchaseOrderLineInput) => !!line.product && (line.id || !line.deleted)
      ) ?? [];

    purchaseOrder.purchaseCosts = purchaseOrder.purchaseCosts?.concat(
      purchaseOrder.purchaseCostsNew ? purchaseOrder.purchaseCostsNew : []
    );
  }

  delete purchaseOrder.headOffice;
  delete purchaseOrder.purchaseCostsNew;

  return purchaseOrder;
};

const PurchaseEdiConfiguration = (props) => {
  const documentConfig = useDocumentConfig();
  const [enabled, setEnabled] = useState<boolean>(false);
  const purchaseOrder = props.values;

  useEffect(
    () => setEnabled(documentConfig?.purchaseOrderDocumentDefault?.sendEdiOrder ?? false),
    [documentConfig?.purchaseOrderDocumentDefault?.sendEdiOrder]
  );

  // Notify parent of the updated `sendEdiOrder` value.
  useEffect(() => {
    if (props.onSendEdiOrderChange) {
      props.onSendEdiOrderChange(enabled);
    }
  }, [enabled, props]);

  if (!purchaseOrder?.supplier?.purchaseEdiConfiguration?.automaticSubmission) {
    return null;
  }

  return (
    <Fieldset cols={1 / 3}>
      <ToggleInput
        label="Send EDI Order"
        value={enabled}
        onChange={(value) => {
          setEnabled(value);
        }}
        labelCols={4 / 5}
        inputCols={1 / 5}
        hint={'Automatically send order via EDI.'}
      />
    </Fieldset>
  );
};

export const PurchaseOrderDocumentRenderer = (props) => {
  const purchaseOrder = normalizeVariables(props.values);
  const [sendEdiOrder, setSendEdiOrder] = useState(false);
  const workspace = useContext(WorkspaceContext);

  const [sendEdiOrderMutation] = useBackgroundMutation<
    ISendEdiPurchaseOrderInput,
    ISendEdiPurchaseOrderMutationVariables
  >(SendEdiPurchaseOrderMutation, {
    jobTitle: 'Send EDI Order',
    variables: {
      input: {
        clientMutationId: generateClientMutationId(),
        purchaseOrderId: purchaseOrder.id,
      },
    },
    onCompleteJob: () => {
      workspace.reloadData?.();
    },
  });

  return (
    <DocumentModal2
      {...props}
      key={'purchaseOrderDocuments'}
      entity={PurchaseOrderDocument}
      useCustomerDefaults={true}
      recipientField="supplier"
      contactField="supplierContact"
      module="Purchases"
      prePrintAction={() => {
        if (sendEdiOrder) {
          sendEdiOrderMutation();
        }
      }}
    >
      <PurchaseEdiConfiguration {...props} onSendEdiOrderChange={setSendEdiOrder} />
    </DocumentModal2>
  );
};
